/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LicensingModule
 */

import { Injectable } from '@angular/core';
import { LicenseTierType } from 'generated-types';
import { pick } from 'underscore';

import { ILicenseSummary, ILicenseTiersUsageData } from '../licensing.types';

interface ILicenseHash {
    [key: string]: ILicenseSummary;
    [LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES]: ILicenseSummary;
    [LicenseTierType.ENTERPRISE]: ILicenseSummary;
}

/**
 * @description Service for Licensing Summary.
 * @author Deepak Chaudhary
 */

@Injectable({
    providedIn: 'root',
})
export class LicenseSummaryService {
    /**
     * Used to make or lookup prop key used.
     */
    public getLicenseHashKey(licenseTierType: LicenseTierType, licenseType: string): string {
        return `${licenseTierType}-${licenseType}`;
    }

    /**
     * Construct list of licenses organized by LicenseType, with LicenseTierType inside. Holds final formatted data
     * for license summary table.
     */
    public getLicenseListByType(
        licenseHash: ILicenseHash,
        licenseTypes: string[],
        licenseTierTypes: LicenseTierType[],
    ): ILicenseSummary[] {
        return licenseTypes.map((licenseType: string) => {
            const licenseHashKey = this.getLicenseHashKey(licenseTierTypes[0], licenseType);
            const tiers = this.populateTiers(licenseHash, licenseType, licenseTierTypes);

            return {
                description: licenseHash[licenseHashKey].description,
                key_: licenseType,
                label: licenseHash[licenseHashKey].label,
                tiers,
            };
        });
    }

    /**
     * Return data for one row in license summary table, by tier.
     */
    public populateTiers(
        licenseHash: ILicenseHash,
        licenseType: string,
        licenseTierTypes: LicenseTierType[],
    ): ILicenseTiersUsageData {
        return licenseTierTypes.reduce((
            accumulator: ILicenseTiersUsageData,
            licenseTierType: LicenseTierType,
        ): ILicenseTiersUsageData => {
            const licenseHashKey = this.getLicenseHashKey(licenseTierType, licenseType);
            const licenseHashVal = licenseHash[licenseHashKey];

            accumulator[licenseTierType] = pick(licenseHashVal, ['total', 'used']);

            return accumulator;
        }, {} as any as ILicenseTiersUsageData);
    }
}
